import { InversionContainer } from '@/controller/container'
import { InjectionTokens } from '@/controller/tokens'

import { UseCase } from '@/domain/core/UseCase'
import { ADHP, CoordWaypoint, Waypoint } from '@/domain/models'
import { Coordinates, CoordinatesProps } from '@/domain/protocols/Coordinates'
import { Result } from '@/domain/protocols/Result'

import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'
import { INavaidRepository } from '@/data/NavaidRepository/INavaidRepository'

import { ILogger } from '@/infra/logger/ILogger'
import { nmPythagoreanDistance } from '@/utils/coordinates'

const PROXIMITY_RADIUS_NM = 20
const PROXIMITY_MAX_RESULTS = 15

// Mesma ponderação distância/prioridade do AeroInfoRepository.searchByProximity*.
const getPriority = (wpt: Waypoint): number => {
  if (wpt.waypointType?.codeName === 'ADHP') {
    return (wpt as ADHP).type === 'AD' ? 4 : 2
  }
  const priorityMap: Record<string, number> = { Navaid: 2, VisualPosition: 2, AwWaypoint: 1, City: 1 }
  return priorityMap[wpt.waypointType?.codeName ?? ''] ?? 1
}

class GetProximityWaypointsUseCase implements UseCase<Coordinates, Result<Waypoint[]>> {
  aeroInfoRepository: IAeroInfoRepository
  navaidRepository: INavaidRepository
  logger: ILogger

  constructor() {
    const injectionContainer = InversionContainer.getInstance().getContainer()
    this.aeroInfoRepository = injectionContainer.get<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository)
    this.navaidRepository = injectionContainer.get<INavaidRepository>(InjectionTokens.NavaidRepository)
    this.logger = injectionContainer.get<ILogger>(InjectionTokens.Logger)
  }

  execute(center: Coordinates): Result<Waypoint[]> {
    try {
      const aeroResults = this.aeroInfoRepository.searchByProximityWithLowPrecision(
        center,
        PROXIMITY_RADIUS_NM,
        PROXIMITY_MAX_RESULTS
      )
      const navaidResults = this.navaidRepository.searchByProximity(center, PROXIMITY_RADIUS_NM, PROXIMITY_MAX_RESULTS)

      const merged = [...aeroResults, ...navaidResults]
        .map((item) => ({ item, distance: nmPythagoreanDistance(center, item.coordinates) }))
        .sort((a, b) => a.distance / getPriority(a.item) - b.distance / getPriority(b.item))
        .slice(0, PROXIMITY_MAX_RESULTS)
        .map(({ item }) => item)

      const resultSet = [CoordWaypoint.create({ coordinates: center }, {}), ...merged]

      return Result.ok(resultSet)
    } catch (error) {
      this.logger.error('GetProximityWaypointsUseCase', String(error))
      return Result.fail(error)
    }
  }
}

export { GetProximityWaypointsUseCase }
